import { isEqual, isString } from 'lodash'
import { removeCommasFromString } from '../utils/removeCommasFromString'
import type { TMSServiceQuantity } from '@/types/msService/models'
import type { TKitQuantity } from '@/types/product/models'
import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'
import { numberOrStringAsNumber } from '~/cynqueModules/utils/numberOrStringAsNumber'

export function getQuantityBasedOnKitQuantity({
  callingFn,
  itemKitQuantity,
  kitMSServiceQuantity,
  itemDoesQualifyToBeHidden,
}: {
  callingFn: string
  itemKitQuantity: TKitQuantity
  kitMSServiceQuantity: number
  itemDoesQualifyToBeHidden: boolean
}): number {
  function _log(message: string) {
    if (
      getPublicRuntimeConfig()
        .doSuppressGetMSQuantityBasedOnKitQuantityLogOutput
    ) {
      return
    }
    console.debug(message)
  }
  const mn = `[cynqueModules/dataObjects/KitQuantity]`
  const fn = `${mn} ${getQuantityBasedOnKitQuantity.name} |`
  _log(
    `${callingFn} ---> ${fn} itemKitQuantity: ${itemKitQuantity},` +
      ` kitMSServiceQuantity: ${kitMSServiceQuantity},` +
      ` itemDoesQualifyToBeHidden: ${itemDoesQualifyToBeHidden}`
  )
  if (itemDoesQualifyToBeHidden) {
    const value = 0
    _log(`${fn} returning ${value}`)
    return value
  } else if (itemKitQuantity === 'kit') {
    const value = kitMSServiceQuantity
    _log(`${fn} returning ${value}`)
    return value
  } else if (
    itemKitQuantity &&
    isString(itemKitQuantity) &&
    itemKitQuantity[0] === 'x'
  ) {
    const multiplier = Number(itemKitQuantity.slice(1))
    if (isNaN(multiplier)) {
      throw new TypeError(`${fn} multiplier is NaN (${itemKitQuantity})`)
    }
    _log(`${fn} multiplier is ${multiplier}`)
    const value = kitMSServiceQuantity * multiplier
    _log(`${fn} returning ${value}`)
    return value
  } else if (kitMSServiceQuantity) {
    const value = numberOrStringAsNumber(itemKitQuantity)
    _log(`${fn} returning ${value}`)
    return value
  } else {
    // kitMSServiceQuantity is 0
    const value = 0
    _log(`${fn} returning ${value}`)
    return value
  }
}

export const validateKitQuantity = (value: string | number) => {
  //
  // empty value evaluates to true to allow empty field
  // to pass validation. Use 'required' validation
  // in addition to this one to validate for presence
  // of value.
  //
  if (isEqual(value, '')) {
    return true || ''
  }
  return (
    (!!value &&
      (typeof value === 'number' ||
        value === 'kit' ||
        (value[0] === 'x' && !isNaN(Number(value.slice(1)))) ||
        +removeCommasFromString(value) === +removeCommasFromString(value))) ||
    kitQuantityHint
  )
}

export const kitQuantityHint =
  "'kit' (follow kit qty); number (fixed value: don't follow kit qty);" +
  " or multiplier ('x1') (multiply by kit qty)"

export const standAloneQuantityHint = 'A whole number of 1 or more.'

//
// A stand-alone item -- or a picked member of a root-level Set -- keeps its
// quantity in `kitQuantity` too, but there is no kit for 'kit' or 'x2' to
// follow, and deriveMSServiceQuantity reads it with Number(), so those, '1,000'
// and blanks-with-junk all came out as quantity 0. Only digits pass.
//
export const validateStandAloneQuantity = (value: string | number) => {
  if (isEqual(value, '')) {
    return true
  }
  const text = String(value).trim()
  return (/^\d+$/.test(text) && Number(text) >= 1) || standAloneQuantityHint
}

//
// The rule for an msService's `kitQuantity` field, which one spec uses for
// rows inside a kit AND for rows that are not (the plain Edit dialog). Inside a
// kit the kit-quantity grammar applies; anywhere else, a whole number.
//
export function getMSServiceQuantityValidator(
  entityData: { parentMSServiceId?: unknown } | undefined
) {
  return entityData?.parentMSServiceId
    ? validateKitQuantity
    : validateStandAloneQuantity
}
